import { CreditCard, Package, Truck, MapPin, CheckCircle2, ShieldAlert, Clock, BanknoteArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n";

/**
 * Buyer-facing order tracking timeline.
 * Stages are derived purely from real order fields — no invented tracking events.
 *
 *   created_at         -> Order placed
 *   paid_at            -> Payment confirmed
 *   (paid, !shipped)   -> Seller preparing (implicit, in-progress)
 *   shipped_at         -> Dispatched
 *   shipped + tracking -> In transit (in-progress, sub-line of Dispatched)
 *   delivered_at       -> Delivered
 *   delivery_confirmed_at OR payout_released_at -> Completed
 *   hasActiveDispute   -> Issue reported (adds a warning step at the end)
 */
export type OrderTimelineInput = {
  created_at?: string | null;
  paid_at?: string | null;
  shipped_at?: string | null;
  tracking_number?: string | null;
  delivered_at?: string | null;
  delivery_confirmed_at?: string | null;
  payout_released_at?: string | null;
  has_active_dispute?: boolean | null;
};

type Step = {
  key: string;
  label: string;
  at: string | null | undefined;
  icon: React.ComponentType<{ className?: string }>;
  active?: boolean;
  variant?: "default" | "warning";
  hint?: string;
};

function fmt(at?: string | null) {
  if (!at) return null;
  try { return new Date(at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return null; }
}

export function OrderTrackingTimeline({ order, className }: { order: OrderTimelineInput; className?: string }) {
  const { t } = useTranslation("commerce");
  const paid = !!order.paid_at;
  const shipped = !!order.shipped_at;
  const delivered = !!order.delivered_at;
  const completed = !!order.delivery_confirmed_at || !!order.payout_released_at;
  const inTransit = shipped && !!order.tracking_number && !delivered;

  const steps: Step[] = [
    { key: "placed", label: t("timeline.placed", "Order placed"), at: order.created_at, icon: Clock },
    { key: "paid", label: t("timeline.paid", "Payment confirmed"), at: order.paid_at, icon: CreditCard },
    {
      key: "preparing",
      label: t("timeline.preparing", "Seller preparing"),
      at: null,
      icon: Package,
      active: paid && !shipped,
      hint: paid && !shipped ? t("timeline.preparingHint", "Awaiting dispatch from seller") : undefined,
    },
    { key: "dispatched", label: t("timeline.dispatched", "Dispatched"), at: order.shipped_at, icon: Truck },
    {
      key: "in_transit",
      label: t("timeline.inTransit", "In transit"),
      at: null,
      icon: MapPin,
      active: inTransit,
      hint: inTransit ? t("timeline.inTransitHint", "Tracking {number}", { number: order.tracking_number ?? "" }) : undefined,
    },
    { key: "delivered", label: t("timeline.delivered", "Delivered"), at: order.delivered_at, icon: CheckCircle2 },
    { key: "completed", label: t("timeline.completed", "Completed"), at: order.delivery_confirmed_at ?? order.payout_released_at, icon: BanknoteArrowUp },
  ];

  if (order.has_active_dispute) {
    steps.push({
      key: "issue",
      label: t("timeline.issueReported", "Issue reported"),
      at: null,
      icon: ShieldAlert,
      active: true,
      variant: "warning",
      hint: t("timeline.issueHint", "Support is reviewing this order"),
    });
  }

  return (
    <ol className={cn("relative border-l border-border/60 pl-6 space-y-4", className)} aria-label={t("timeline.ariaLabel", "Order status timeline")}>
      {steps.map((s) => {
        const done = !!s.at;
        const active = !!s.active && !done;
        const warning = s.variant === "warning";
        const Icon = s.icon;
        return (
          <li key={s.key} className="relative">
            <span
              className={cn(
                "absolute -left-[31px] top-0.5 h-5 w-5 rounded-full flex items-center justify-center transition-colors",
                warning
                  ? "bg-amber-500 text-black shadow-[0_0_0_4px_rgba(245,158,11,0.15)]"
                  : done
                  ? "bg-emerald-500 text-white shadow-[0_0_0_4px_rgba(16,185,129,0.15)]"
                  : active
                  ? "bg-primary text-primary-foreground shadow-[0_0_0_4px_rgba(139,92,246,0.18)] animate-pulse"
                  : "bg-surface-2 text-muted-foreground border border-border",
              )}
              aria-hidden
            >
              <Icon className="h-3 w-3" />
            </span>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
              <span
                className={cn(
                  "text-sm",
                  done ? "text-foreground" : active ? "text-foreground font-medium" : "text-muted-foreground",
                  warning && "text-amber-400 font-medium",
                )}
              >
                {s.label}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {fmt(s.at) ?? (active ? t("timeline.inProgress", "In progress") : "—")}
              </span>
            </div>
            {s.hint && (
              <p className="mt-0.5 text-[11px] text-muted-foreground">{s.hint}</p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
